/**
 * `/settings` - WHAT THE PUBLIC MAY SEE ABOUT THE AUTHOR, AND CLOSING THE ACCOUNT.
 *
 * Register R-016 (lens findings C1-02, U1-04; birth data owed since decision-log row 216 (3)). Five
 * served operations had no caller anywhere in the app: `setBirthData`, `clearBirthData`,
 * `setComputedSignsVisibility`, `setEarningsVisibility` and `requestAccountDeletion`. The
 * earnings consent is private by default (authors-api.yaml, `setEarningsVisibility`), so with no
 * screen no author could ever opt in.
 *
 * ONE READ, `getOwnProfile`, for what it carries that this page uses: `earningsVisibility`, and the
 * author name the deletion asks to be typed. The section then keeps the state its own write answers
 * with, so a switch shows what the platform recorded rather than what was clicked.
 *
 * ★ "YOUR CHART" IS NOT HERE ANY MORE (design 24, T19): birth data and the star signs are the last
 * card of the Author profile page (features/application/components/BirthDataSection.tsx).
 *
 * THREE SECTIONS, each with its own failure: a refused switch does not touch the deletion. The
 * middle one, "Your agreements" (design 25), reads the documents the author has accepted
 * (./AgreementsSection.tsx). Plain words throughout; layout and wording are the app's own
 * (decision-log rows 99 and 117).
 *
 * ★ DESIGN 15 (the owner's live look at real cabinets, 2026-10-01). The prototype does not draw this
 * page; it is laid out as the prototype lays out the author profile - each section a card with a
 * header strip (the kit's SectionCard), a small word at its right saying who can see what it holds
 * ("Public" in gold, "Private"), taken from what the platform RECORDED, never from the click.
 * "The platform" became "MoonX" in what an author reads. Every read, write, refusal and state is
 * as it was.
 */
import { Stack, Switch, Text } from '@mantine/core';
import { Coins } from '@moonx/ui';
import { useCallback, useState } from 'react';
import { useCabinetApi } from '../../features/cabinet/api';
import type { OperationResult } from '../../features/cabinet/api';
import {
  CabinetPageHeader,
  ErrorState,
  LoadingState,
  SectionCard,
} from '../../features/cabinet/components';
import { useAction, useAsync } from '../../features/cabinet/state';
import { formatInstant } from '../../features/cabinet/format';
import { AccountDeletionSection } from './AccountDeletionSection';
import { AgreementsSection } from './AgreementsSection';
import { useTranslation } from '../../i18n';

/** This screen's address, mounted by `features/cabinet/routes.ts`. */
export const SETTINGS_PATH = '/settings';

type EarningsVisibility = OperationResult<'setEarningsVisibility'>;

/** An author who never chose: off, with no moment of change - the contract's default. */
const NEVER_CHOSEN: EarningsVisibility = { enabled: false, changedAt: null };

export function SettingsRoute() {
  const { t } = useTranslation('profile');
  const api = useCabinetApi();
  const readProfile = useCallback((signal: AbortSignal) => api.getOwnProfile(signal), [api]);
  const profile = useAsync(readProfile, 'settings:own-profile');

  return (
    <Stack gap="lg">
      <CabinetPageHeader title={t('settings.title')} lede={t('settings.lede')} />

      {profile.status === 'loading' && profile.data === undefined ? (
        <LoadingState label={t('settings.reading')} rows={3} />
      ) : profile.status === 'error' && profile.data === undefined ? (
        <ErrorState title={t('settings.failed')} error={profile.error} onRetry={profile.refetch} />
      ) : profile.data === undefined ? null : (
        <EarningsVisibilitySection initial={profile.data.earningsVisibility ?? NEVER_CHOSEN} />
      )}

      {/* Its own read (design 25): what the author has agreed to, and any new version waiting. */}
      <AgreementsSection />

      {/* Not behind the profile read: it asks for the author name the read carries once it has
          answered, and for the session's until then (design 24, T12). */}
      <AccountDeletionSection authorName={profile.data?.displayName} />
    </Stack>
  );
}

function EarningsVisibilitySection({ initial }: { readonly initial: EarningsVisibility }) {
  const { t } = useTranslation('profile');
  const api = useCabinetApi();
  const [state, setState] = useState<EarningsVisibility>(initial);
  const save = useAction((enabled: boolean) => api.setEarningsVisibility({ enabled }));

  return (
    <SectionCard
      title={t('settings.earnings.title')}
      icon={Coins}
      tag={
        state.enabled
          ? { words: t('settings.earnings.public'), tone: 'public' }
          : { words: t('settings.earnings.private'), tone: 'quiet' }
      }
    >
      <Stack gap="sm">
        <Switch
          label={t('settings.earnings.switch')}
          checked={state.enabled}
          disabled={save.isRunning}
          onChange={(event) => {
            const enabled = event.currentTarget.checked;
            void save.run(enabled).then((recorded) => {
              if (recorded !== undefined) {
                setState(recorded);
              }
            });
          }}
        />
        <Text size="xs" c="dimmed">
          {state.changedAt === undefined || state.changedAt === null
            ? t('settings.earnings.note')
            : t('settings.earnings.noteChanged', { when: formatInstant(state.changedAt) })}
        </Text>
        {save.error === undefined ? null : (
          <ErrorState title={t('settings.earnings.notChanged')} error={save.error} />
        )}
      </Stack>
    </SectionCard>
  );
}
